import { Box, Button, Typography, useMediaQuery } from '@mui/material';
import SelectDistance from './components/SelectDistance/SelectDistance';
import PromoCode from './components/PromoCode/PromoCode';
import Insurance from './components/Insurance/Insurance';
import Products from './components/Products/Products';
import { Dispatch, FC, SetStateAction, useState } from 'react';
import Summary from '../../Summary/Summary';
import { useAppSelector } from 'shared/lib/hooks/hooks';
import RegistrationInfo from './components/RegistrationInfo/RegistrationInfo';
import { useTranslation } from 'react-i18next';
import { useTheme, Theme } from '@mui/material/styles';
import OfferAgreement from './components/OfferAgreement/OfferAgreement';
import { ConfirmModal } from 'shared/ui';
import { postPurchaseInsurances } from 'api/thunk/insuranceAgreements/postActions/postPurchaseInsurances';
import { useAppDispatch } from 'shared/config/configureStore';
import { useParams } from 'react-router-dom';
import { BuyInsurance } from 'assets/icons';
import { FormProvider, useForm } from 'react-hook-form';
import ProductInfo from './components/ProductInfo/ProductInfo';
interface IRegistrationTabProps {
  setEnteredPromocode: (value: string) => void;
  enteredPromocode: string;
  step: 1 | 2 | 3;
  setStep: Dispatch<SetStateAction<1 | 2 | 3>>;
  changeDistance: boolean;
  setChangeDistance: (value: boolean) => void;
  handleOpenChangeDistanceModal: () => void;
}

const RegistrationTab: FC<IRegistrationTabProps> = ({
  setEnteredPromocode,
  enteredPromocode,
  setStep,
  step,
  changeDistance,
  setChangeDistance,
  handleOpenChangeDistanceModal
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { id } = useParams();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const methods = useForm({
    defaultValues: {
      offerAccepted: false,
    },
  });

  const marathon = useAppSelector((state) => state.marathons.marathonsById.response.data);
  const hasInsurance = useAppSelector((state) => state.marathons.marathonsHasUserInsurance.response.data);
  const registrationInfo = useAppSelector((state) => state.marathons.userRegistered.data);
  const hasUserInsurance = useAppSelector((state) => state.marathons.marathonsHasUserInsurance.response.data);
  const userMarathonProducts = useAppSelector((state) => state.products.userMarathonProducts.response.data);
  const theme = useTheme<Theme>();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const isInsuranceNeeded = marathon?.distances?.find((distance) => distance?.id === Number(registrationInfo?.distanceId))?.hasInsurance;

  const handleBuyInsurance = async () => {
    dispatch(postPurchaseInsurances({ marathonId: Number(id) }))
  }

  const handleOpenModel = () => {
    setIsModalOpen(true)
  }

  const handleIsModalClose = () => {
    setIsModalOpen(false)
  }

  return (
    <FormProvider {...methods}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "16px"
        }}
      >
        {registrationInfo && !changeDistance ? (
          <>
            {
              registrationInfo && !hasInsurance && isInsuranceNeeded && (
                <section className={`container-personalCabinet`}>
                  <Box sx={{
                    minWidth: 0,
                    width: { xs: '100%', sm: 'auto' },
                    display: 'flex',
                    alignItems: 'end',
                    gap: 2,
                    justifyContent: 'space-between',
                    flexWrap: "wrap"
                  }}>
                    <Box
                      sx={{
                        background: '#FFECEB',
                        borderRadius: '12px',
                        display: 'flex',
                        columnGap: '8px',
                        alignItems: 'center',
                        padding: '6px 9px',
                        width: '50%',
                        maxWidth: "330px",
                        minWidth: 0,
                        flex: '1 1 auto',
                      }}>
                      <BuyInsurance width={26} height={26} />
                      <Typography
                        sx={{
                          fontFamily: 'Raleway',
                          fontWeight: 600,
                          fontSize: '12px',
                          color: '#E53B35',
                          textTransform: 'none !important',
                          wordBreak: 'break-word',
                          whiteSpace: 'normal',
                          lineHeight: '16px',
                        }}
                      >
                        {t("myMarathon.buyInsurance.warningBox.text")}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        width: { xs: '100%', sm: 'auto' },
                        flexShrink: 0,
                      }}>
                      <Button
                        variant="contained"
                        onClick={handleOpenModel}
                        sx={{
                          fontFamily: 'Raleway',
                          fontWeight: 500,
                          minHeight: '36px',
                          fontSize: '14px',
                          lineHeight: '24px',
                          background: '#0090D1',
                          borderRadius: '12px',
                          padding: '8px 16px',
                          wordWrap: 'wrap',
                          whiteSpace: 'normal',
                          "&:hover": {
                            backgroundColor: "#007BB3",
                          },
                        }}
                      >{t("myMarathon.buyInsurance.warningBox.buttonText")}</Button>
                    </Box>
                  </Box>
                </section>
              )
            }
            <RegistrationInfo
              setChangeDistance={setChangeDistance}
            />
          </>
        ) : (
          <>
            {step === 1 && (
              <>
                <SelectDistance
                  changeDistance={changeDistance}
                />
                <OfferAgreement />
                <PromoCode
                  setEnteredPromocode={setEnteredPromocode}
                  enteredPromocode={enteredPromocode}
                />
                {!hasUserInsurance && (
                  <Insurance />
                )}
                {userMarathonProducts !== null && userMarathonProducts.length > 0 && (
                  <ProductInfo />
                )}
                <Box sx={{
                  paddingBottom: isMobile ? "20px" : "200px",
                  alignSelf: "flex-end",
                }}>
                  {!isMobile && step === 1 && (
                    <Box sx={{ paddingBottom: "200px", alignSelf: "flex-end" }}>
                      <Button
                        variant="contained"
                        sx={{
                          mt: 2,
                          alignSelf: "flex-end",
                          background: "#0090D1",
                          borderRadius: "12px",
                          padding: "8px 32px",
                          "&:hover": {
                            backgroundColor: "#007BB3",
                          },
                        }}
                        onClick={methods.handleSubmit(() => setStep(2))}
                      >
                        {t("createMarathon.next")}
                      </Button>
                    </Box>
                  )}
                </Box>
              </>
            )}
            {step === 2 && (
              <Box>
                <Products />
                <Box sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  columnGap: "30px",
                  paddingBottom: isMobile ? "20px" : "200px",
                }}>

                  {!isMobile && step === 2 && (
                    <>
                      <Button
                        variant="outlined"
                        sx={{
                          mt: 2,
                          color: "#0090D1",
                          border: "1px solid #0090D1",
                          borderRadius: "12px",
                          padding: "8px 32px",

                          "&:hover": {
                            color: "#007BB3",
                            borderColor: "#007BB3",
                            backgroundColor: "transparent",
                          }
                        }}
                        onClick={() => setStep(1)}
                      >
                        {t("createMarathon.back")}
                      </Button>
                      <Button
                        variant="contained"
                        sx={{
                          mt: 2,
                          background: "#0090D1",
                          borderRadius: "12px",
                          padding: "8px 32px",

                          "&:hover": {
                            backgroundColor: "#007BB3",
                          }
                        }}
                        onClick={() => setStep(3)}
                      >
                        {t("createMarathon.next")}
                      </Button>
                    </>
                  )}
                </Box>
              </Box>
            )}
            {step === 3 && (
              <Box sx={{
                gap: {
                  xs: "0",
                  md: "0"
                }
              }} >
                <Summary
                  showButton={false}
                  setChangeDistance={setChangeDistance}
                  changeDistance={changeDistance}
                />
                <Box sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  columnGap: "30px",
                  paddingBottom: isMobile ? "20px" : "200px",
                }}>
                  {!isMobile && step === 3 && (<>
                    <Button
                      variant="outlined"
                      sx={{
                        mt: 2,
                        color: "#0090D1",
                        border: "1px solid #0090D1",
                        borderRadius: "12px",
                        padding: "8px 32px",

                        "&:hover": {
                          color: "#007BB3",
                          borderColor: "#007BB3",
                          backgroundColor: "transparent",
                        }
                      }}
                      onClick={() => setStep(2)}
                    >
                      {t("createMarathon.back")}
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => handleOpenChangeDistanceModal()}
                      sx={{
                        mt: 2,
                        background: "#0090D1",
                        borderRadius: "12px",
                        padding: "8px 32px",

                        "&:hover": {
                          backgroundColor: "#007BB3",
                        }
                      }}
                    >
                      {t("buttons.proceedToPayment")}
                    </Button>
                  </>)}
                </Box>
              </Box>
            )}
          </>
        )}
        <ConfirmModal
          open={isModalOpen}
          title={t("myMarathon.buyInsurance.modal.title")}
          description={t("myMarathon.buyInsurance.modal.description", { price: marathon?.insurancePrice })}
          confirmText={t("myMarathon.buyInsurance.modal.confirmText")}
          cancelText={t("myMarathon.buyInsurance.modal.cancelText")}
          onConfirm={handleBuyInsurance}
          onCancel={handleIsModalClose}
        />
      </Box >
    </FormProvider>
  );
};

export default RegistrationTab;